import { useMutation, useQuery } from '@apollo/client'
import { useRef, useState } from 'react'
import toast from 'react-hot-toast'

import { UPDATE_FEATURE_FLAG } from 'data/layoutcreator/mutations/featureFlags'
import { GET_FEATURE_FLAGS } from 'data/layoutcreator/queries/featureFlags'
import {
  FeatureFlagEntity,
  FeatureFlagState
} from 'shared/types/layoutcreator/graphql'
import Button from 'shared/ui/Button_2'
import Flex from 'shared/ui/Flex'
import Text from 'shared/ui/Text'

import FeatureFlagRow from './components/FeatureFlagRow'

const FeatureFlags = () => {
  const [featureFlags, setFeatureFlags] = useState<
    FeatureFlagEntity[]
  >([])
  const updatedFlags = useRef<string[]>([])

  const { data } = useQuery(GET_FEATURE_FLAGS, {
    onCompleted: data => {
      setFeatureFlags(data.featureFlags)
      updatedFlags.current = []
    }
  })

  const [updateFeatureFlag] = useMutation(UPDATE_FEATURE_FLAG)

  const handleStateChange = (
    flagKey: string,
    newState: FeatureFlagState
  ) => {
    updatedFlags.current.push(flagKey)
    setFeatureFlags(
      featureFlags.map(flag =>
        flag.key === flagKey ? { ...flag, state: newState } : flag
      )
    )
  }

  const handleSave = async () => {
    const results: boolean[] = []
    
    for (const flag of featureFlags) {
      if (!updatedFlags.current.includes(flag.key)) {
        continue
      }

      const { data } = await updateFeatureFlag({
        variables: {
          input: {
            key: flag.key,
            state: flag.state,
            limitedGroupIds: flag.limitedGroupIds
          }
        }
      })

      results.push(data?.updateFeatureFlag !== null)
    }

    if (results.every(result => result)) {
      toast.success('Feature flags updated')
      updatedFlags.current = []
    } else {
      toast.error('Failed to update feature flags')
    }
  }

  return (
    <Flex direction="column" marginTop={88} marginBottom={360}>
      <Text text="Feature Flags" size={28} weight={600} />
      <Flex direction="column" marginTop={36}>
        {featureFlags.map((flag: FeatureFlagEntity) => (
          <FeatureFlagRow
            key={flag.key}
            flagKey={flag.key}
            name={flag.name}
            description={flag.description}
            state={flag.state}
            limitedGroupIds={flag.limitedGroupIds}
            onStateChange={newState =>
              handleStateChange(flag.key, newState)
            }
          />
        ))}
      </Flex>

      <Flex justifyContent="flex-end" marginTop={36}>
        {updatedFlags.current.length > 0 && (
          <Button textId="Save" intent='primary-brand-02' onPress={() => handleSave()} />
        )}
      </Flex>
    </Flex>
  )
}

export default FeatureFlags
